import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchStoreStatus } from '../api/storeStatusApi';
import KitchenClosedDialog from '../components/KitchenClosedDialog/KitchenClosedDialog';
import { defaultStoreStatus } from '../utils/storeStatus';

const POLL_MS = 5 * 60 * 1000;

const StoreStatusContext = createContext(null);

export function StoreStatusProvider({ children }) {
  const [status, setStatus] = useState(defaultStoreStatus);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchStoreStatus();
      setStatus(next);
      return next;
    } catch {
      /* Keep the last known status; default remains open so a fetch failure
         does not lock checkout. Matches mobile's `?? true` fallback. */
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(() => {
      refresh();
    }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const resolveDialog = useCallback((ok) => {
    setDialogOpen(false);
    pendingRef.current?.resolve(ok);
    pendingRef.current = null;
  }, []);

  const confirmAddWhileClosed = useCallback(() => {
    if (status.isStoreOpen) return Promise.resolve(true);
    if (pendingRef.current) pendingRef.current.resolve(false);
    return new Promise((resolve) => {
      pendingRef.current = { resolve };
      setDialogOpen(true);
    });
  }, [status.isStoreOpen]);

  const value = useMemo(
    () => ({
      status,
      isKitchenOpen: status.isStoreOpen,
      refresh,
      confirmAddWhileClosed,
    }),
    [status, refresh, confirmAddWhileClosed],
  );

  return (
    <StoreStatusContext.Provider value={value}>
      {children}
      <KitchenClosedDialog
        open={dialogOpen}
        onClose={() => resolveDialog(false)}
        onProceed={() => resolveDialog(true)}
      />
    </StoreStatusContext.Provider>
  );
}

export function useStoreStatus() {
  const context = useContext(StoreStatusContext);
  if (!context) {
    throw new Error('useStoreStatus must be used within StoreStatusProvider');
  }
  return context;
}
